import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import {
  ArrowRight,
  ArrowsOutSimple,
  Briefcase,
  ChatCircleDots,
  CircleNotch,
  ClipboardText,
  CopySimple,
  DotsSixVertical,
  Lightning,
  MagicWand,
  Minus,
  Plus,
  Sparkle,
  Star,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { Button, Portal, Separator, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";

import { useBreakpoint } from "@reactive-resume/hooks";

import { useFeatureGate } from "../hooks/use-feature-gate";
import { useTakingLonger } from "../hooks/use-taking-longer";
import { displayStreamingText, parseApplySummary } from "../libs/ai-apply-summary";
import { askAssistantToApply } from "../libs/ai-assist";
import {
  ASSISTANT_BOTTOM,
  ASSISTANT_GAP,
  ASSISTANT_MIN_H,
  ASSISTANT_MIN_W,
  assistantDefaultFrame,
  assistantMaxSize,
  clampAssistantPosition,
  clampAssistantSize,
} from "../libs/ai-assistant-layout";
import { resumeHasRealWork } from "../libs/resume-has-real-work";
import { getAiStatus, processAi, processAiStream, reportAiOutcome } from "../services/ai";
import { ChatMessage, useAiStore } from "../stores/ai";
import { useAuthStore } from "../stores/auth";
import { useResumeStore } from "../stores/resume";

const quickActions = [
  { id: "review", icon: Star, label: () => t`Review My Resume`, description: () => t`Get a score and actionable feedback`, action: "review-resume" as const },
  { id: "summary", icon: MagicWand, label: () => t`Write My Summary`, description: () => t`Generate a professional summary`, action: "generate-summary" as const },
  { id: "improve", icon: Sparkle, label: () => t`Suggest Improvements`, description: () => t`Get tips to strengthen each section`, action: "suggest-improvements" as const },
  { id: "tailor", icon: Briefcase, label: () => t`Tailor to a Job`, description: () => t`Paste a posting for specific resume edits`, action: "tailor-to-job" as const },
];

const MIN_W = ASSISTANT_MIN_W;
const MIN_H = ASSISTANT_MIN_H;

const defaultFrame = () => {
  if (typeof window === "undefined") {
    return assistantDefaultFrame({ innerWidth: 1440, innerHeight: 900 });
  }
  return assistantDefaultFrame({
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
  });
};

const clampSize = (w: number, h: number) => {
  if (typeof window === "undefined") return { w, h };
  return clampAssistantSize(w, h, window.innerWidth, window.innerHeight);
};

const clampPosition = (right: number, bottom: number, w: number, h: number) => {
  if (typeof window === "undefined") return { right, bottom };
  return clampAssistantPosition(right, bottom, w, h, window.innerWidth, window.innerHeight);
};

type Edge = "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se";

const edgeCursors: Record<Edge, string> = {
  n: "cursor-n-resize", s: "cursor-s-resize", e: "cursor-e-resize", w: "cursor-w-resize",
  nw: "cursor-nw-resize", ne: "cursor-ne-resize", sw: "cursor-sw-resize", se: "cursor-se-resize",
};

const ScoreRing = ({ score }: { score: number }) => {
  const r = 36, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  const clr = score >= 80 ? "text-emerald-500" : score >= 60 ? "text-amber-500" : "text-rose-500";
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="88" height="88" className="-rotate-90">
        <circle cx="44" cy="44" r={r} fill="none" stroke="currentColor" strokeWidth="5" className="text-secondary-accent/60" />
        <motion.circle cx="44" cy="44" r={r} fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" className={clr} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: off }} transition={{ duration: 1.2, ease: "easeOut" }} strokeDasharray={c} />
      </svg>
      <motion.span className={cn("absolute text-xl font-bold tabular-nums", clr)} initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.3 }}>{score}</motion.span>
    </div>
  );
};

const ContextBadge = ({ message }: { message: ChatMessage }) => (
  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end">
    <div className="flex items-center gap-1.5 rounded-lg border border-primary/15 bg-primary/5 px-2.5 py-1.5">
      <ClipboardText size={12} weight="fill" className="shrink-0 text-primary/60" />
      <span className="text-[11px] font-medium text-primary/70">{message.contextLabel}</span>
    </div>
  </motion.div>
);

const stripHtmlTags = (html: string) => html.replace(/<[^>]*>/g, "").trim();

const dailyLimitMessage = () =>
  t`You've reached your daily AI limit. It resets automatically tomorrow.`;

const AssistantCopy = ({ text }: { text: string }) => (
  <span className="break-words [overflow-wrap:anywhere]">
    {text.split(/(\*\*[^*\n]+\*\*)/g).map((chunk, index) =>
      chunk.startsWith("**") && chunk.endsWith("**") ? (
        <strong key={index} className="font-semibold">
          {chunk.slice(2, -2)}
        </strong>
      ) : (
        <span key={index}>{chunk}</span>
      ),
    )}
  </span>
);

const Bubble = ({ message }: { message: ChatMessage }) => {
  if (message.type === "context") return <ContextBadge message={message} />;
  const u = message.role === "user";
  const [copied, setCopied] = useState(false);
  const displayText = !u ? stripHtmlTags(message.content) : message.content;
  const showCopy = !u && displayText.length > 40;

  const handleCopy = () => {
    navigator.clipboard.writeText(displayText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={cn("group flex", u ? "justify-end" : "justify-start")}>
      <div className="flex min-w-0 max-w-[85%] flex-col">
        <div
          className={cn(
            "whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed [overflow-wrap:anywhere]",
            u ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md bg-secondary-accent/60 text-foreground",
          )}
        >
          {u ? displayText : <AssistantCopy text={displayText} />}
        </div>
        {showCopy && (
          <div className="mt-0.5 flex justify-start opacity-0 transition-opacity group-hover:opacity-100">
            <button onClick={handleCopy} className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:bg-secondary-accent hover:text-foreground">
              <CopySimple size={10} />
              <span>{copied ? t`Copied!` : t`Copy`}</span>
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Suggestion = ({ text, i, onApply }: { text: string; i: number; onApply: (text: string) => void }) => (
  <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * i }} className="flex items-start gap-2.5 rounded-lg border border-primary/10 bg-primary/5 p-3">
    <Lightning size={14} weight="fill" className="mt-0.5 shrink-0 text-primary" />
    <div className="min-w-0 flex-1">
      <p className="text-xs leading-relaxed text-foreground/80">{text}</p>
      <button
        type="button"
        onClick={() => onApply(text)}
        className="mt-1.5 text-[10px] font-medium text-primary hover:underline"
      >
        {t`Fix this in chat`}
      </button>
    </div>
  </motion.div>
);

export const AiAssistant = () => {
  const panelOpen = useAiStore((s) => s.panelOpen);
  const setPanelOpen = useAiStore((s) => s.setPanelOpen);
  const minimized = useAiStore((s) => s.panelMinimized);
  const setMinimized = useAiStore((s) => s.setPanelMinimized);
  const messages = useAiStore((s) => s.chatMessages);
  const addMsg = useAiStore((s) => s.addMessage);
  const clearChat = useAiStore((s) => s.clearChat);
  const busy = useAiStore((s) => s.isProcessing);
  const setBusy = useAiStore((s) => s.setProcessing);
  const [retrying, setRetrying] = useState(false);
  const takingLonger = useTakingLonger(busy) || retrying;
  const setReview = useAiStore((s) => s.setReviewResult);
  const reviewScore = useAiStore((s) => s.lastReviewScore);
  const reviewSugs = useAiStore((s) => s.lastReviewSuggestions);
  const resumeData = useResumeStore((s) => s.resume.data);
  const setValue = useResumeStore((s) => s.setValue);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const ownerUserId = useAiStore((s) => s.ownerUserId);
  const chatReady = Boolean(userId && ownerUserId === userId);
  const visibleMessages = chatReady ? messages : [];
  const rateLimit = useAiStore((s) => s.rateLimit);
  const setRateLimit = useAiStore((s) => s.setRateLimit);
  const pendingSend = useAiStore((s) => s.pendingSend);
  const setPendingSend = useAiStore((s) => s.setPendingSend);

  const { isMobile } = useBreakpoint();
  const { gate } = useFeatureGate();

  const [input, setInput] = useState("");
  const [view, setView] = useState<"chat" | "review">("chat");
  const [tailorMode, setTailorMode] = useState(false);
  const [queued, setQueued] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState("");
  const [pendingSummary, setPendingSummary] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const posRef = useRef({ right: ASSISTANT_GAP, bottom: ASSISTANT_BOTTOM });
  const sizeRef = useRef({ w: ASSISTANT_MIN_W, h: ASSISTANT_MIN_H });
  const [, forceRender] = useState(0);
  const rerender = () => forceRender((n) => n + 1);

  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [entered, setEntered] = useState(false);
  const hasOpened = useRef(false);

  useEffect(() => {
    const apply = () => {
      const id = useAuthStore.getState().user?.id ?? userId;
      if (id) useAiStore.getState().bindUser(id);
    };
    apply();
    return useAiStore.persist.onFinishHydration(apply);
  }, [userId]);

  useEffect(() => {
    setView("chat");
    setTailorMode(false);
    setQueued(null);
    setInput("");
  }, [userId]);

  useEffect(() => {
    if (visibleMessages.length === 0) setView("chat");
  }, [visibleMessages.length]);

  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (event.defaultPrevented) return;
      setPanelOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelOpen, setPanelOpen]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [visibleMessages, streamingText]);
  useEffect(() => {
    if (!panelOpen) return;
    void getAiStatus()
      .then((status) => {
        if (status.rateLimit) setRateLimit(status.rateLimit);
      })
      .catch(() => undefined);
  }, [panelOpen, setRateLimit]);

  useEffect(() => {
    if (panelOpen && !minimized && inputRef.current) setTimeout(() => inputRef.current?.focus(), 300);
  }, [panelOpen, minimized]);

  useEffect(() => {
    if (!panelOpen) return;
    const frame = defaultFrame();
    posRef.current = { right: frame.right, bottom: frame.bottom };
    if (!hasOpened.current) {
      sizeRef.current = { w: frame.w, h: frame.h };
      hasOpened.current = true;
    } else {
      sizeRef.current = clampSize(sizeRef.current.w, sizeRef.current.h);
    }
    setEntered(false);
    requestAnimationFrame(() => setEntered(true));
    rerender();
  }, [panelOpen]);

  useEffect(() => {
    const onWinResize = () => {
      sizeRef.current = clampSize(sizeRef.current.w, sizeRef.current.h);
      posRef.current = clampPosition(
        posRef.current.right,
        posRef.current.bottom,
        sizeRef.current.w,
        sizeRef.current.h,
      );
      rerender();
    };
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, []);

  // --- Drag ---
  const onDragDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button, input, a")) return;
    if (minimized || isMobile) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    setDragging(true);
    const sx = e.clientX, sy = e.clientY;
    const sp = { ...posRef.current };

    const mv = (ev: PointerEvent) => {
      const next = clampPosition(
        sp.right - (ev.clientX - sx),
        sp.bottom - (ev.clientY - sy),
        sizeRef.current.w,
        sizeRef.current.h,
      );
      posRef.current = next;
      rerender();
    };
    const up = () => {
      setDragging(false);
      el.removeEventListener("pointermove", mv);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("lostpointercapture", up);
    };
    el.addEventListener("pointermove", mv);
    el.addEventListener("pointerup", up);
    el.addEventListener("lostpointercapture", up);
  }, [minimized, isMobile]);

  // --- Resize from any edge/corner ---
  const onEdgeDown = useCallback((edge: Edge, e: React.PointerEvent<HTMLElement>) => {
    if (isMobile) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    setResizing(true);

    const sx = e.clientX, sy = e.clientY;
    const sw = sizeRef.current.w, sh = sizeRef.current.h;
    const sp = { ...posRef.current };
    const growN = edge.includes("n");
    const growS = edge.includes("s");
    const growW = edge.includes("w");
    const growE = edge.includes("e");

    const mv = (ev: PointerEvent) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      let nw = sw, nh = sh, nRight = sp.right, nBottom = sp.bottom;
      const { maxW, maxH } =
        typeof window === "undefined"
          ? { maxW: 800, maxH: 900 }
          : assistantMaxSize(window.innerWidth, window.innerHeight);

      if (growW) {
        nw = Math.max(MIN_W, Math.min(maxW, sw - dx));
        nRight = sp.right + (nw - sw);
      }
      if (growE) {
        nw = Math.max(MIN_W, Math.min(maxW, sw + dx));
      }
      if (growN) {
        nh = Math.max(MIN_H, Math.min(maxH, sh - dy));
      }
      if (growS) {
        nh = Math.max(MIN_H, Math.min(maxH, sh + dy));
        nBottom = sp.bottom - (nh - sh);
      }

      sizeRef.current = clampSize(nw, nh);
      posRef.current = clampPosition(nRight, nBottom, sizeRef.current.w, sizeRef.current.h);
      rerender();
    };
    const up = () => {
      setResizing(false);
      el.removeEventListener("pointermove", mv);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("lostpointercapture", up);
    };
    el.addEventListener("pointermove", mv);
    el.addEventListener("pointerup", up);
    el.addEventListener("lostpointercapture", up);
  }, [isMobile]);

  const handleRateLimitResponse = useCallback((rl: typeof rateLimit) => {
    if (rl) {
      setRateLimit(rl);
    }
  }, [setRateLimit]);

  const send = useCallback(async (text: string) => {
    if (!text.trim() || !chatReady) return;
    if (busy) {
      setQueued(text.trim());
      setInput("");
      return;
    }

    const requiredFeature = tailorMode ? "auto-tailor" : "ai-chat";
    if (!gate(requiredFeature)) return;

    if (rateLimit?.blocked) {
      addMsg({ id: createId(), role: "assistant", content: dailyLimitMessage(), timestamp: Date.now() });
      return;
    }

    addMsg({ id: createId(), role: "user", content: text.trim(), timestamp: Date.now() });
    setInput("");
    setBusy(true);
    setRetrying(false);
    setStreamingText("");
    setPendingSummary(null);
    try {
      const contextMessages = messages.filter((m) => m.type === "context").slice(-1);
      const extraContext = contextMessages.length > 0 ? contextMessages[0].content : undefined;
      const r = await processAiStream(
        {
          action: tailorMode ? "tailor-to-job" : "chat",
          content: text.trim(),
          jobDescription: tailorMode ? text.trim() : undefined,
          context: { resumeData, ...(extraContext ? { sectionData: extraContext } : {}) },
          chatHistory: messages.filter((m) => m.type !== "context").slice(-10).map((m) => ({ role: m.role, content: m.content })),
        },
        (full) => setStreamingText(full),
        {
          onRetry: () => {
            setRetrying(true);
            setStreamingText("");
          },
        },
      );

      handleRateLimitResponse(r.rateLimit ?? null);
      reportAiOutcome(r.callId, "used");
      const parsed = parseApplySummary(r.result);
      addMsg({ id: createId(), role: "assistant", content: parsed.display || r.result, timestamp: Date.now() });
      if (parsed.summary) setPendingSummary(parsed.summary);

      if (tailorMode) setTailorMode(false);
    } catch (error) {
      const fallback = error instanceof Error && error.message && error.message !== "AI stream failed"
        ? error.message
        : t`Sorry, I encountered an error. Please try again.`;
      addMsg({ id: createId(), role: "assistant", content: fallback, timestamp: Date.now() });
    } finally {
      setStreamingText("");
      setRetrying(false);
      setBusy(false);
    }
  }, [busy, addMsg, setBusy, messages, resumeData, tailorMode, rateLimit, handleRateLimitResponse, gate, chatReady]);

  useEffect(() => {
    if (pendingSend && panelOpen && !busy && chatReady) {
      const text = pendingSend;
      setPendingSend(null);
      send(text);
    }
  }, [pendingSend, panelOpen, busy, chatReady, send, setPendingSend]);

  useEffect(() => {
    if (!busy && queued) {
      const text = queued;
      setQueued(null);
      send(text);
    }
  }, [busy, queued, send]);

  const quickAct = useCallback(async (a: (typeof quickActions)[number]) => {
    if (a.action === "tailor-to-job") {
      setTailorMode(true);
      setView("chat");
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    if (busy || !chatReady) return;

    const featureKey = a.action === "review-resume" || a.action === "suggest-improvements" ? "ats-score" : "ai-full";
    if (!gate(featureKey)) return;

    if (rateLimit?.blocked) {
      addMsg({ id: createId(), role: "assistant", content: dailyLimitMessage(), timestamp: Date.now() });
      return;
    }

    if (a.action === "generate-summary" && !resumeHasRealWork(resumeData)) {
      addMsg({
        id: createId(),
        role: "assistant",
        content: t`Add a real job first. I will write a summary from your experience, not a fictional one.`,
        timestamp: Date.now(),
      });
      return;
    }

    if (a.action === "review-resume") setView("review");
    addMsg({ id: createId(), role: "user", content: a.label(), timestamp: Date.now() });
    setBusy(true);
    try {
      const r = await processAi({ action: a.action, context: { resumeData } });
      handleRateLimitResponse(r.rateLimit ?? null);
      if (r.ok === false) {
        addMsg({
          id: createId(),
          role: "assistant",
          content: r.result || t`Sorry, I encountered an error. Please try again.`,
          timestamp: Date.now(),
        });
        return;
      }
      reportAiOutcome(r.callId, "used");

      if (a.action === "review-resume") {
        setReview(r.score ?? null, r.suggestions ?? []);
        const scoreText = typeof r.score === "number" ? t`Your resume scored ${r.score}/100.` + " " : "";
        addMsg({
          id: createId(),
          role: "assistant",
          content: `${scoreText}${r.result}`,
          timestamp: Date.now(),
        });
      } else if (a.action === "suggest-improvements") {
        setReview(null, r.suggestions ?? []);
        addMsg({
          id: createId(),
          role: "assistant",
          content: r.result,
          timestamp: Date.now(),
        });
      } else if (a.action === "generate-summary" && r.result) {
        setValue("sections.summary.content", r.result);
        const plainSummary = stripHtmlTags(r.result);
        addMsg({
          id: createId(),
          role: "assistant",
          content: t`I've written a professional summary and placed it in your Summary section. Here's what I wrote:` + `\n\n${plainSummary}`,
          timestamp: Date.now(),
        });
      } else {
        addMsg({ id: createId(), role: "assistant", content: r.result, timestamp: Date.now() });
      }
    } catch {
      addMsg({ id: createId(), role: "assistant", content: t`Sorry, I encountered an error. Please try again.`, timestamp: Date.now() });
    } finally { setBusy(false); }
  }, [busy, addMsg, setBusy, setReview, resumeData, setValue, rateLimit, handleRateLimitResponse, gate, chatReady]);

  const onKey = (e: React.KeyboardEvent) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } };
  const onHeaderClick = useCallback(() => { if (minimized) setMinimized(false); }, [minimized, setMinimized]);

  const resetSession = useCallback(() => {
    clearChat();
    setView("chat");
    setReview(null, []);
    setTailorMode(false);
    setInput("");
    setPendingSummary(null);
    setStreamingText("");
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [clearChat, setReview]);

  if (!panelOpen) return null;

  const frame = defaultFrame();
  if (!hasOpened.current) {
    sizeRef.current = { w: frame.w, h: frame.h };
    posRef.current = { right: frame.right, bottom: frame.bottom };
  }
  const { right, bottom } = posRef.current;
  const { w, h } = sizeRef.current;

  const edgeHandle = (edge: Edge, cls: string) => (
    <div
      key={edge}
      onPointerDown={(e) => onEdgeDown(edge, e)}
      style={{ touchAction: "none" }}
      className={cn("absolute z-30", edgeCursors[edge], cls)}
    />
  );

  return (
    <Portal>
      <div
        data-ai-assistant
        role="dialog"
        aria-modal="false"
        aria-labelledby="ai-assistant-title"
        style={isMobile ? {
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          opacity: entered ? 1 : 0,
          transition: "opacity 0.15s",
        } : {
          position: "fixed",
          right,
          bottom,
          top: "auto",
          left: "auto",
          width: minimized ? frame.w : w,
          height: minimized ? "auto" : h,
          zIndex: 9999,
          opacity: entered ? 1 : 0,
          transition: dragging || resizing ? "none" : "opacity 0.2s",
        }}
        className={cn(
          "flex min-w-0 flex-col overflow-hidden bg-background shadow-2xl",
          isMobile ? "rounded-none" : "rounded-2xl border",
          dragging && "ring-2 ring-primary/20",
          resizing && "select-none",
        )}
      >
        {/* Edge resize handles (desktop only, non-minimized) */}
        {!minimized && !isMobile && (
          <>
            {edgeHandle("n", "inset-x-3 top-0 h-2 hover:bg-primary/15")}
            {edgeHandle("s", "inset-x-3 bottom-0 h-2 hover:bg-primary/15")}
            {edgeHandle("w", "inset-y-3 left-0 w-2 hover:bg-primary/15")}
            {edgeHandle("e", "inset-y-3 right-0 w-2 hover:bg-primary/15")}
            {edgeHandle("nw", "top-0 left-0 h-4 w-4")}
            {edgeHandle("ne", "top-0 right-0 h-4 w-4")}
            {edgeHandle("sw", "bottom-0 left-0 h-4 w-4")}
            {edgeHandle("se", "bottom-0 right-0 h-8 w-8")}
            <button
              type="button"
              onPointerDown={(e) => onEdgeDown("se", e)}
              title={t`Drag to resize`}
              aria-label={t`Drag to resize`}
              style={{ touchAction: "none" }}
              className="absolute bottom-1 right-1 z-40 flex h-7 w-7 cursor-se-resize items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-secondary hover:text-foreground"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden className="opacity-90">
                <path d="M11 1v10H1" fill="none" stroke="currentColor" strokeWidth="1.7" />
                <path d="M11 5v6H5" fill="none" stroke="currentColor" strokeWidth="1.7" />
                <path d="M11 8v3H8" fill="none" stroke="currentColor" strokeWidth="1.7" />
              </svg>
            </button>
          </>
        )}

        {/* Header */}
        <div
          onPointerDown={onDragDown}
          onClick={onHeaderClick}
          style={{ touchAction: "none" }}
          className={cn(
            "flex shrink-0 items-center justify-between border-b bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 px-4 py-2.5",
            minimized ? "cursor-pointer" : isMobile ? "cursor-default" : dragging ? "cursor-grabbing" : "cursor-grab",
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            {!isMobile && !minimized && (
              <DotsSixVertical size={16} className="shrink-0 text-muted-foreground/70" aria-hidden />
            )}
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Sparkle size={14} weight="fill" className="text-primary" />
            </div>
            <div className="min-w-0">
              <h3 id="ai-assistant-title" className="text-sm font-semibold leading-tight">{t`AI Assistant`}</h3>
              {minimized && <p className="text-[10px] leading-tight text-muted-foreground">{t`Click to expand`}</p>}
              {!minimized && !isMobile && (
                <p className="truncate text-[11px] leading-tight text-muted-foreground">
                  {t`Drag to move · Corner to resize`}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1">
            {visibleMessages.length > 0 && !minimized && (
              <Tooltip content={t`Start a new conversation`}>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1.5 px-2 text-[11px] font-medium text-muted-foreground hover:bg-background/80 hover:text-foreground"
                  onClick={(e) => { e.stopPropagation(); resetSession(); }}
                >
                  <Plus size={12} weight="bold" />
                  <span className="hidden sm:inline">{t`New Chat`}</span>
                </Button>
              </Tooltip>
            )}
            {!isMobile && (
              <Tooltip content={minimized ? t`Expand` : t`Minimize`}>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}>
                  {minimized ? <ArrowsOutSimple size={14} /> : <Minus size={14} />}
                </Button>
              </Tooltip>
            )}
            <Tooltip content={t`Close`}>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setPanelOpen(false); }}>
                <X size={14} />
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Body */}
        {!minimized && (
          <div className="flex min-h-0 flex-1 flex-col">
            <div ref={scrollRef} className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-x-hidden overflow-y-auto overscroll-contain px-4 py-4">
                {visibleMessages.length === 0 && view === "chat" && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-4 py-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5">
                      <MagicWand size={24} className="text-primary" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium">{t`How can I help with your resume?`}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{t`Ask me anything, or try a quick action below`}</p>
                    </div>
                    <div className="mt-2 grid w-full gap-2">
                      {quickActions.map((a) => (
                        <button key={a.id} onClick={() => quickAct(a)} disabled={busy} className={cn("group flex items-center gap-3 rounded-xl border border-transparent bg-secondary-accent/40 px-3.5 py-3 text-left transition-all", "hover:border-primary/20 hover:bg-primary/5 hover:shadow-sm", "disabled:pointer-events-none disabled:opacity-50")}>
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background shadow-sm transition-colors group-hover:bg-primary/10">
                            <a.icon size={15} weight="duotone" className="text-muted-foreground transition-colors group-hover:text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium leading-tight">{a.label()}</p>
                            <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{a.description()}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {visibleMessages.map((m) => <Bubble key={m.id} message={m} />)}

                {view === "review" && reviewScore !== null && (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center gap-3 rounded-xl border bg-gradient-to-b from-secondary-accent/30 to-transparent p-4">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t`Resume Score`}</p>
                    <ScoreRing score={reviewScore} />
                    <p className="text-xs text-muted-foreground">
                      {reviewScore >= 80 ? t`Excellent! Your resume is strong.` : reviewScore >= 60 ? t`Good start. A few tweaks will make it great.` : t`Let's work on improving your resume together.`}
                    </p>
                  </motion.div>
                )}

                {view === "review" && reviewSugs.length > 0 && (
                  <div className="grid gap-2">
                    <p className="text-xs font-medium text-muted-foreground">{t`Suggestions`}</p>
                    {reviewSugs.map((s, i) => <Suggestion key={i} text={s} i={i} onApply={askAssistantToApply} />)}
                  </div>
                )}

                {streamingText && (
                  <div className="flex justify-start">
                    <div className="min-w-0 max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-md bg-secondary-accent/60 px-3.5 py-2.5 text-sm leading-relaxed [overflow-wrap:anywhere]">
                      <AssistantCopy text={stripHtmlTags(displayStreamingText(streamingText))} />
                    </div>
                  </div>
                )}

                {busy && !streamingText && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-1.5">
                    <div className="flex justify-start">
                      <div className="flex items-center gap-2 rounded-2xl rounded-bl-md bg-secondary-accent/60 px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          {[0, 0.2, 0.4].map((d) => <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60" animate={{ opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 1.2, delay: d }} />)}
                        </div>
                        <span className="text-xs text-muted-foreground/70">
                          {takingLonger ? t`Taking longer than usual…` : t`Thinking...`}
                        </span>
                      </div>
                    </div>
                    {queued && (
                      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-1.5 pl-1">
                        <CircleNotch size={10} className="animate-spin text-muted-foreground/50" />
                        <span className="text-[10px] text-muted-foreground/50">{t`Your next message is queued`}</span>
                      </motion.div>
                    )}
                  </motion.div>
                )}

                {/* In-conversation "New Chat" prompt */}
                <AnimatePresence>
                  {visibleMessages.length >= 2 && !busy && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ delay: 0.5, duration: 0.3 }}
                      className="flex flex-col items-center gap-1.5 pt-2"
                    >
                      <div className="h-px w-12 bg-border/60" />
                      <button
                        onClick={resetSession}
                        className="group flex items-center gap-1.5 rounded-full border border-border/40 bg-background px-3 py-1.5 text-[11px] font-medium text-muted-foreground shadow-sm transition-all hover:border-primary/30 hover:bg-primary/5 hover:text-primary hover:shadow-md"
                      >
                        <Plus size={11} weight="bold" className="transition-transform group-hover:rotate-90" />
                        <span>{t`Reset conversation`}</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
            </div>

            <Separator />

            {tailorMode && (
              <div className="flex items-start justify-between gap-2 border-b bg-primary/5 px-3 py-2">
                <p className="text-[11px] leading-snug text-foreground/80">
                  {t`Paste the job description below. I will suggest concrete edits from your current resume. Example metrics go in [brackets] for you to edit.`}
                </p>
                <button
                  type="button"
                  className="shrink-0 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                  onClick={() => setTailorMode(false)}
                >
                  {t`Cancel`}
                </button>
              </div>
            )}

            {pendingSummary && (
              <div className="flex items-start justify-between gap-2 border-b bg-primary/5 px-3 py-2">
                <p className="text-[11px] leading-snug text-foreground/80">
                  {t`Replace your Summary with this rewrite? Example numbers in [brackets] should be edited to your real figures.`}
                </p>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    className="text-[11px] font-medium text-primary hover:underline"
                    onClick={() => {
                      setValue("sections.summary.content", pendingSummary);
                      setPendingSummary(null);
                    }}
                  >
                    {t`Apply to Summary`}
                  </button>
                  <button
                    type="button"
                    className="text-[11px] font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setPendingSummary(null)}
                  >
                    {t`Dismiss`}
                  </button>
                </div>
              </div>
            )}

            {/* Rate limit warning */}
            {rateLimit?.warning && (
              <div className="flex items-center gap-1.5 border-b border-amber-200 bg-amber-50 px-3 py-1.5 dark:border-amber-800 dark:bg-amber-950/30">
                <WarningCircle size={12} className="shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="text-[10px] text-amber-700 dark:text-amber-300">{rateLimit.warning}</span>
              </div>
            )}

            <div className="shrink-0 p-3 pr-9">
              <div className={cn("flex items-center gap-2 rounded-xl border bg-secondary-accent/30 px-3 py-2 transition-colors", "focus-within:border-primary/30 focus-within:bg-background focus-within:ring-1 focus-within:ring-primary/20")}>
                <ChatCircleDots size={16} className="shrink-0 text-muted-foreground" />
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={busy ? t`Type your next message (will send when ready)...` : tailorMode ? t`Paste the job description here...` : t`Ask about your resume...`}
                  disabled={rateLimit?.blocked}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
                />
                <Tooltip content={busy ? t`Queue message` : t`Send`}>
                  <button onClick={() => send(input)} disabled={!input.trim() || rateLimit?.blocked} className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all", input.trim() ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90" : "text-muted-foreground/40")}>
                    {busy && !input.trim() ? <CircleNotch size={14} className="animate-spin" /> : <ArrowRight size={14} weight="bold" />}
                  </button>
                </Tooltip>
              </div>
              <p className="mt-1.5 text-[10px] text-muted-foreground/70">
                {rateLimit
                  ? t`${rateLimit.used} of ${rateLimit.limit} AI actions today`
                  : t`AI actions reset every day`}
              </p>
            </div>
          </div>
        )}
      </div>
    </Portal>
  );
};
